import { useState } from 'react';
import { isExcludedByKeywords } from '../utils/matchingLogic';

// Free-text keyword entry for want-list exclusions. There was no tag input
// anywhere in the app when this was written — the effect-type pickers look
// similar but toggle a fixed list rather than accepting typed values.
//
// Chips are red rather than the gold used for want criteria elsewhere, because
// these mean the opposite: "never show me this". Reading an exclusion as a want
// would be a genuinely confusing mistake.

export const MAX_EXCLUSION_KEYWORDS = 20;
const MIN_KEYWORD_LENGTH = 2;
const MAX_KEYWORD_LENGTH = 30;

// Stored lowercase and whitespace-collapsed so the list the user sees is the
// same text the matcher compares against.
const normalizeKeyword = (raw) => String(raw || '').toLowerCase().replace(/\s+/g, ' ').trim();

// Copied from the Max Price / Min Condition / Decade fields in AddWantForm so
// this input is visually indistinguishable from its neighbours. The pill radius
// and 17px type are the form's convention; a bare Tailwind input reads as
// noticeably smaller and heavier next to them.
const textInputStyle = {
  borderRadius: '25px',
  fontSize: '17px',
  verticalAlign: 'middle',
  borderWidth: '1px',
  fontWeight: 'normal',
  width: '100%',
  paddingTop: '0.33rem',
  paddingBottom: '0.33rem',
  display: 'block',
  boxSizing: 'border-box'
};

const chipStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '6px',
  padding: '6px 8px 6px 14px',
  borderRadius: '9999px',
  border: '1.5px solid #fecaca',
  backgroundColor: '#fef2f2',
  color: '#b91c1c',
  fontWeight: '600',
  fontSize: '0.9rem',
  fontFamily: 'Nunito, sans-serif',
  maxWidth: '100%'
};

const removeButtonStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '20px',
  height: '20px',
  borderRadius: '9999px',
  border: 'none',
  backgroundColor: 'rgba(185, 28, 28, 0.12)',
  color: '#b91c1c',
  fontSize: '0.85rem',
  lineHeight: 1,
  cursor: 'pointer',
  padding: 0,
  flexShrink: 0
};

function KeywordExclusionInput({ value = [], onChange, disabled = false, wantCriteria = null }) {
  const [draft, setDraft] = useState('');
  const [error, setError] = useState('');

  const atCapacity = value.length >= MAX_EXCLUSION_KEYWORDS;

  // Each entry becomes its own keyword. Typing a comma commits immediately, but
  // this also has to split, because pasting a list is the other obvious way in
  // and no keystroke ever fires for it — "behringer, blue, vintage" pasted and
  // then blurred would otherwise be stored as one nonsense term that matches
  // nothing, and it would look like it worked.
  const commitDraft = () => {
    const pieces = draft.split(/[,\n]/).map(normalizeKeyword).filter(Boolean);
    if (pieces.length === 0) {
      setDraft('');
      setError('');
      return;
    }

    const next = [...value];
    let tooShort = false;
    let tooLong = false;
    let duplicate = false;
    let full = false;

    for (const keyword of pieces) {
      if (keyword.length < MIN_KEYWORD_LENGTH) { tooShort = true; continue; }
      if (keyword.length > MAX_KEYWORD_LENGTH) { tooLong = true; continue; }
      if (next.includes(keyword)) { duplicate = true; continue; }
      if (next.length >= MAX_EXCLUSION_KEYWORDS) { full = true; continue; }
      next.push(keyword);
    }

    if (next.length !== value.length) onChange(next);
    setDraft('');

    if (full) setError(`You can exclude up to ${MAX_EXCLUSION_KEYWORDS} keywords.`);
    else if (tooLong) setError(`Keywords are limited to ${MAX_KEYWORD_LENGTH} characters.`);
    else if (tooShort) setError(`Keywords need at least ${MIN_KEYWORD_LENGTH} characters.`);
    else if (duplicate) setError('That keyword is already on the list.');
    else setError('');
  };

  const removeKeyword = (keyword) => {
    onChange(value.filter((k) => k !== keyword));
    setError('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      // Enter would otherwise submit the surrounding want form.
      e.preventDefault();
      commitDraft();
    } else if (e.key === 'Backspace' && draft === '' && value.length > 0) {
      removeKeyword(value[value.length - 1]);
    }
  };

  // A keyword that excludes the want's own criteria makes the want unmatchable —
  // wanting a delay under $100 while excluding "delay" can never return
  // anything. Checked by running the real matcher against the want's own
  // brand/model/effect type, so this warning cannot drift from the behaviour.
  const selfDefeating = wantCriteria
    ? value.filter((keyword) => isExcludedByKeywords({ excludedKeywords: [keyword] }, wantCriteria))
    : [];

  return (
    <div>
      {value.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '0.85rem' }}>
          {value.map((keyword) => (
            <span key={keyword} style={chipStyle}>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {keyword}
              </span>
              <button
                type="button"
                onClick={() => removeKeyword(keyword)}
                disabled={disabled}
                aria-label={`Stop excluding ${keyword}`}
                style={removeButtonStyle}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}

      <input
        type="text"
        value={draft}
        onChange={(e) => {
          setDraft(e.target.value);
          if (error) setError('');
        }}
        onKeyDown={handleKeyDown}
        onBlur={commitDraft}
        disabled={disabled || atCapacity}
        placeholder={atCapacity ? 'Keyword limit reached' : 'e.g. Behringer, blue, modified'}
        aria-label="Add a keyword to exclude from matches"
        className="border border-gray-300 focus:border-blue-500 focus:outline-none px-3"
        style={textInputStyle}
      />

      <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.8rem', color: error ? '#b91c1c' : '#9ca3af', fontFamily: 'Nunito, sans-serif', lineHeight: 1.45, textAlign: 'left' }}>
        {error || 'Separate keywords with a comma or press Enter. Any listing whose brand, model, colour, effect type or description mentions one is skipped.'}
      </p>

      {selfDefeating.length > 0 && (
        <div style={{
          marginTop: '0.75rem',
          padding: '10px 14px',
          borderRadius: '12px',
          backgroundColor: '#fffbeb',
          border: '1px solid #fde68a',
          color: '#92400e',
          fontSize: '0.8rem',
          fontFamily: 'Nunito, sans-serif',
          lineHeight: 1.45
        }}>
          <strong>
            {selfDefeating.map((k) => `"${k}"`).join(', ')}{' '}
            {selfDefeating.length === 1 ? 'cancels' : 'cancel'} out this want.
          </strong>{' '}
          {selfDefeating.length === 1 ? 'It rules' : 'They rule'} out the very pedal you are asking for, so this want will never match anything.
        </div>
      )}
    </div>
  );
}

export default KeywordExclusionInput;
